import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Loader2, Check, Crown, Star } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Header } from '../components/Header';
import { API_URL, cn } from '../lib/utils';

export default function SubscriptionPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/plans`, { credentials: 'include' })
      .then(r => r.json())
      .then(setPlans)
      .catch(console.error);
  }, []);

  const handleUpgrade = async (planId) => {
    if (planId === 'free') return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ plan_id: planId, origin_url: window.location.origin })
      });
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.url;
      }
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-main">
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-muted hover:text-foreground mb-6">
            <ArrowLeft className="w-5 h-5" /> Back
          </button>

          <div className="text-center mb-12">
            <h1 className="font-heading text-4xl font-bold text-foreground mb-4">Choose Your Plan</h1>
            <p className="text-muted text-lg max-w-2xl mx-auto">
              Start free with LinkedIn, upgrade for unlimited sprints & comments, or go premium for full quiz generation.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {/* FREE TIER */}
            <div className={cn("card relative", user?.subscription === 'free' && "ring-2 ring-border")}>
              {user?.subscription === 'free' && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-border text-foreground text-xs font-medium">Current Plan</span>
              )}
              <div className="text-center mb-6">
                <h3 className="font-heading text-2xl font-bold text-foreground mb-2">Free</h3>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-bold text-foreground">$0</span>
                </div>
                <p className="text-xs text-muted mt-1">LinkedIn login only</p>
              </div>
              <ul className="space-y-3 mb-8">
                {plans.free?.features?.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-foreground"><Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" /> {f}</li>
                ))}
              </ul>
              <button disabled className="w-full py-3 rounded-full font-medium bg-card-hover text-muted cursor-not-allowed text-sm">
                {user?.subscription === 'free' ? 'Current Plan' : 'Free Forever'}
              </button>
            </div>

            {/* PRO TIER */}
            <div className={cn("card relative border-2 bg-gradient-to-b from-primary/5 to-transparent", user?.subscription === 'pro' ? "border-amber-500 ring-2 ring-amber-400" : "border-primary")}>
              {user?.subscription === 'pro' ? (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-amber-500 text-white text-xs font-medium flex items-center gap-1"><Crown className="w-3 h-3" /> Current</span>
              ) : (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-primary text-white text-xs font-medium flex items-center gap-1"><Star className="w-3 h-3" /> Popular</span>
              )}
              <div className="text-center mb-6">
                <h3 className="font-heading text-2xl font-bold text-foreground mb-2 flex items-center justify-center gap-2">
                  Pro <Crown className="w-5 h-5 text-amber-500" />
                </h3>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-bold text-foreground">${plans.pro?.price || 16.99}</span>
                  <span className="text-muted">/month</span>
                </div>
                <p className="text-xs text-muted mt-1">LinkedIn + Google login</p>
              </div>
              <ul className="space-y-3 mb-8">
                {plans.pro?.features?.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-foreground"><Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" /> {f}</li>
                ))}
              </ul>
              <button onClick={() => handleUpgrade('pro')} disabled={loading || user?.subscription === 'pro'} className={cn("w-full py-3 rounded-full font-medium flex items-center justify-center gap-2 text-sm", user?.subscription === 'pro' ? "bg-card-hover text-muted cursor-not-allowed" : "btn-primary")}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : user?.subscription === 'pro' ? 'Current Plan' : 'Upgrade to Pro'}
              </button>
            </div>

            {/* PREMIUM TIER */}
            <div className={cn("card relative border-2 bg-gradient-to-b from-purple-500/5 to-transparent", user?.subscription === 'premium' ? "border-purple-500 ring-2 ring-purple-400" : "border-purple-500/50")}>
              {user?.subscription === 'premium' && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-purple-500 text-white text-xs font-medium flex items-center gap-1"><Star className="w-3 h-3" /> Current</span>
              )}
              <div className="text-center mb-6">
                <h3 className="font-heading text-2xl font-bold text-foreground mb-2 flex items-center justify-center gap-2">
                  Premium <Star className="w-5 h-5 text-purple-500" />
                </h3>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-bold text-foreground">${plans.premium?.price || 26.99}</span>
                  <span className="text-muted">/month</span>
                </div>
                <p className="text-xs text-muted mt-1">All features unlocked</p>
              </div>
              <ul className="space-y-3 mb-8">
                {plans.premium?.features?.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-foreground"><Check className="w-4 h-4 text-purple-500 mt-0.5 shrink-0" /> {f}</li>
                ))}
              </ul>
              <button onClick={() => handleUpgrade('premium')} disabled={loading || user?.subscription === 'premium'} className={cn("w-full py-3 rounded-full font-medium flex items-center justify-center gap-2 text-sm", user?.subscription === 'premium' ? "bg-card-hover text-muted cursor-not-allowed" : "bg-purple-500 hover:bg-purple-600 text-white")}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : user?.subscription === 'premium' ? 'Current Plan' : 'Upgrade to Premium'}
              </button>
            </div>
          </div>

          <p className="text-center text-muted text-sm mt-8">Secure payment via Stripe. Cancel anytime.</p>
        </motion.div>
      </main>
    </div>
  );
}
